import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Clock, User, ArrowLeft, Copy, MessageCircle as MsgIcon, ThumbsUp, Link as LinkIcon } from 'lucide-react';
import { Button } from '../components/ui/button.jsx';
import { api } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { useInnerCircle } from '../context/InnerCircleContext';
import { toast } from 'sonner';
import WhatsAppCTA from '../components/WhatsAppCTA';
import SEO from '../components/SEO';
import { authUrlWithNext } from '../lib/whatsapp';
import InnerCircleBottomBar from '../components/InnerCircleBottomBar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Gate card shown at the end of truncated content for Inner Circle only posts.
 * Non-members see this. Members see the full article.
 */
const GateCard = () => {
  const navigate = useNavigate();
  return (
    <div
      className="relative mt-4 mb-10 card-mystical rounded-2xl p-6 sm:p-8 text-center"
      data-testid="blog-gate-card"
    >
      <div className="text-[11px] font-heading tracking-[0.3em] text-primary/55 uppercase mb-3">
        Inner Circle
      </div>
      <h3 className="text-xl sm:text-2xl font-heading golden-text text-glow tracking-wide mb-2">
        This writing is shared with the Inner Circle.
      </h3>
      <p className="text-sm text-primary/75 italic mb-6">Join to read the rest.</p>
      <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
        <button
          type="button"
          onClick={() => { navigate('/inner-circle'); }}
          className="ornate-button px-6 py-2.5 text-sm font-heading uppercase tracking-[0.2em]"
          data-testid="blog-gate-join"
        >
          Join the Inner Circle
        </button>
        <button
          type="button"
          onClick={() => navigate(authUrlWithNext(null, window.location.pathname))}
          className="text-xs text-primary/70 hover:text-primary underline underline-offset-4"
          data-testid="blog-gate-signin"
        >
          Already a member? Sign in.
        </button>
      </div>
    </div>
  );
};

/**
 * Short tail line at the end of every open article — gentle invitation.
 */
const ArticleEndJoinLine = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  if (user?.is_member) return null;
  return (
    <div className="mt-4 mb-10 text-center" data-testid="blog-article-end-join">
      <p className="text-sm text-primary/70 italic mb-3 max-w-xl mx-auto leading-relaxed">
        More writing from the inside is shared with the Inner Circle.
      </p>
      <button
        type="button"
        onClick={() => navigate('/inner-circle')}
        className="ornate-button px-5 py-2 text-xs font-heading uppercase tracking-[0.25em]"
        data-testid="blog-article-end-join-cta"
      >
        Join the Inner Circle
      </button>
    </div>
  );
};

const BlogPost = () => {
  const { postId } = useParams(); // actually the slug
  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [likedByMe, setLikedByMe] = useState(false);
  const [likePending, setLikePending] = useState(false);
  const [showLoginToLike, setShowLoginToLike] = useState(false);
  const [copyLabel, setCopyLabel] = useState('Copy Link');
  const [related, setRelated] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, token } = useAuth();

  const fetchPost = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/blog/${postId}`, { headers });
      setPost(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [postId]);

  const fetchComments = useCallback(async () => {
    if (!post?.id) return;
    try {
      const data = await api.getComments(post.id);
      setComments(data);
    } catch (e) {
      console.error(e);
    }
  }, [post?.id]);

  useEffect(() => { fetchPost(); }, [fetchPost]);
  useEffect(() => { fetchComments(); }, [fetchComments]);

  // Likes — fetch on post + user change
  const fetchLikes = useCallback(async () => {
    if (!post?.slug) return;
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/blog/${post.slug}/likes`, { headers });
      setLikeCount(Number(res.data?.count || 0));
      setLikedByMe(Boolean(res.data?.liked_by_me));
    } catch (e) {
      // non-fatal; engagement bar just shows 0
    }
  }, [post?.slug, token]);

  useEffect(() => { fetchLikes(); }, [fetchLikes]);

  // Related posts
  useEffect(() => {
    if (!post?.slug) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/blog/${post.slug}/related?limit=2`);
        if (!cancelled) setRelated(Array.isArray(res.data) ? res.data : []);
      } catch (e) {
        if (!cancelled) setRelated([]);
      }
    })();
    return () => { cancelled = true; };
  }, [post?.slug]);

  const toggleLike = async () => {
    if (!user) {
      setShowLoginToLike(true);
      return;
    }
    if (likePending || !post?.slug) return;
    setLikePending(true);
    setShowLoginToLike(false);
    // Optimistic update
    const prevCount = likeCount;
    const prevLiked = likedByMe;
    setLikedByMe(!prevLiked);
    setLikeCount(Math.max(0, prevCount + (prevLiked ? -1 : 1)));
    try {
      const res = await axios.post(
        `${API}/blog/${post.slug}/like/toggle`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setLikeCount(Number(res.data?.count || 0));
      setLikedByMe(Boolean(res.data?.liked_by_me));
    } catch (e) {
      // revert
      setLikeCount(prevCount);
      setLikedByMe(prevLiked);
      toast.error('Could not update like');
    } finally {
      setLikePending(false);
    }
  };

  const pageUrl = typeof window !== 'undefined' ? window.location.href : '';
  // For social sharing, use the OG trampoline URL so WhatsApp / Facebook / etc.
  // see rich preview tags (their crawlers don't run JS). Humans get auto-redirected
  // to the canonical /blog/:slug page.
  const shareUrl = post
    ? (typeof window !== 'undefined'
      ? `${window.location.origin}/api/og/blog/${post.slug}`
      : `https://myshaktiverse.com/api/og/blog/${post.slug}`)
    : pageUrl;
  const waLink = post
    ? `https://api.whatsapp.com/send?text=${encodeURIComponent(`${post.title} — ${shareUrl}`)}`
    : '#';

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Copied ✓');
    } catch {
      toast.error('Could not copy');
    }
  };

  const copyLinkBottom = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopyLabel('Copied ✦');
      setTimeout(() => setCopyLabel('Copy Link'), 2000);
    } catch {
      toast.error('Could not copy');
    }
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!user) { toast.error('Please sign in to comment'); navigate(authUrlWithNext(location)); return; }
    if (!newComment.trim()) { toast.error('Please write a comment'); return; }
    setSubmitting(true);
    try {
      await api.createComment(post.id, newComment);
      setNewComment('');
      toast.success('Comment shared!');
      fetchComments();
    } catch (err) {
      toast.error('Failed to post comment');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center mystical-bg">
        <div className="text-primary/70">Loading...</div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center mystical-bg">
        <div className="text-primary/70">Post not found</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen mystical-bg py-32">
      <SEO
        title={`${post.title} — MyShaktiverse`}
        description={post.subtitle || post.title}
        image={post.cover_image_url}
        type="article"
        path={`/blog/${post.slug}`}
        publishedAt={post.published_at}
        author={post.author_name}
      />
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Button
            onClick={() => navigate('/blog')}
            variant="ghost"
            className="mb-8 text-primary hover:text-primary/80"
            data-testid="back-to-blog-button"
          >
            <ArrowLeft size={16} className="mr-2" />
            Back to Blog
          </Button>

          <div className="mb-8">
            <div className="flex items-center gap-4 text-sm text-primary/70 mb-4 flex-wrap">
              <span className="px-4 py-1 rounded-full bg-primary/10 text-primary font-medium uppercase tracking-wider border border-primary/25">
                {post.category}
              </span>
              <div className="flex items-center gap-1"><Clock size={14} /><span>{post.read_time} min read</span></div>
              <div className="flex items-center gap-1"><User size={14} /><span>{post.author_name}</span></div>
              {user?.is_admin && (
                <button
                  type="button"
                  onClick={() => navigate(`/blog/edit/${post.slug}`)}
                  className="ml-auto inline-flex items-center gap-1 px-3 py-1 rounded-full border border-primary/30 text-primary/85 hover:bg-primary/10 text-xs"
                  data-testid="blog-post-edit-btn"
                >
                  Edit
                </button>
              )}
            </div>

            <h1 className="text-4xl md:text-5xl font-heading font-semibold golden-text tracking-wider mb-4" data-testid="blog-post-title">
              {post.title}
            </h1>
            {post.subtitle && <p className="text-xl text-primary/80 italic">{post.subtitle}</p>}

            {/* Share row */}
            <div className="flex items-center gap-3 mt-6">
              <a
                href={waLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-primary/30 text-primary/85 hover:bg-primary/10 transition text-sm"
                data-testid="blog-share-whatsapp"
              >
                <MsgIcon size={16} /> Share on WhatsApp
              </a>
              <button
                type="button"
                onClick={copyLink}
                title="Copy a share-ready link (gives a rich preview on WhatsApp, Facebook, etc.)"
                className="group relative inline-flex items-center gap-2 px-4 py-2 rounded-full border border-amber-300/40 text-amber-100/90 bg-amber-400/[0.06] hover:bg-amber-400/[0.12] hover:border-amber-300/70 hover:text-amber-50 transition-all duration-300 text-sm shadow-[0_0_14px_-6px_rgba(212,168,67,0.55)] hover:shadow-[0_0_22px_-4px_rgba(212,168,67,0.85)] focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-300/60"
                data-testid="blog-share-copy"
              >
                <Copy size={16} className="opacity-80 group-hover:opacity-100" />
                Copy share link
              </button>
            </div>
            <p className="mt-2 text-[11px] text-primary/50 italic">
              Tip — use this link when sharing on WhatsApp for a rich preview.
            </p>
          </div>

          {post.cover_image_url && (
            <div className="aspect-[16/9] rounded-2xl overflow-hidden mb-12 border border-primary/20">
              <img src={post.cover_image_url} alt="" className="w-full h-full object-cover" />
            </div>
          )}

          <div className="relative" data-testid="blog-post-content-wrap">
            <div className="relative">
              <div
                className="prose-blog text-primary/90 mb-6"
                data-testid="blog-post-content"
                dangerouslySetInnerHTML={{ __html: post.content || '' }}
              />
              {post.is_gated && (
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-x-0 bottom-0 h-40"
                  style={{
                    background:
                      'linear-gradient(to bottom, rgba(10,6,2,0) 0%, rgba(10,6,2,0.6) 55%, rgba(10,6,2,0.98) 100%)',
                  }}
                />
              )}
            </div>
            {post.is_gated && <GateCard />}
            {!post.is_gated && <ArticleEndJoinLine />}
          </div>

          {/* Engagement bar — sits between article end and Share Your Experience */}
          <div
            className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-8"
            data-testid="blog-engagement-bar"
          >
            <button
              type="button"
              onClick={toggleLike}
              disabled={likePending}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border text-sm transition-colors disabled:opacity-50 ${
                likedByMe
                  ? 'border-amber-300/70 text-amber-200 bg-amber-400/10'
                  : 'border-primary/30 text-primary/85 hover:bg-primary/10'
              }`}
              data-testid="blog-engagement-like"
              aria-pressed={likedByMe}
            >
              <ThumbsUp size={16} className={likedByMe ? 'fill-current' : ''} />
              <span>{likeCount}</span>
            </button>
            <a
              href={waLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-primary/30 text-primary/85 hover:bg-primary/10 transition text-sm"
              data-testid="blog-engagement-whatsapp"
            >
              <MsgIcon size={16} /> Share on WhatsApp
            </a>
            <button
              type="button"
              onClick={copyLinkBottom}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-primary/30 text-primary/85 hover:bg-primary/10 transition text-sm"
              data-testid="blog-engagement-copy"
            >
              <LinkIcon size={16} /> {copyLabel}
            </button>
          </div>
          {showLoginToLike && (
            <p
              className="text-center text-xs text-primary/60 italic mb-6"
              data-testid="blog-engagement-login-hint"
            >
              Login to like this article.
            </p>
          )}

          {/* Comments */}
          <div className="pt-4">
            <h2 className="text-3xl font-heading text-primary mb-8 flex items-center gap-3">
              <MsgIcon size={28} />
              Share Your Experience ({comments.length})
            </h2>

            {user ? (
              <form onSubmit={handleSubmitComment} className="mb-12">
                <div className="card-mystical rounded-xl p-6">
                  <textarea
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Share your thoughts, experiences, or insights..."
                    className="w-full bg-transparent text-primary placeholder-primary/40 border border-primary/30 rounded-lg p-4 mb-4 min-h-32 focus:outline-none focus:border-primary/60"
                    data-testid="comment-textarea"
                  />
                  <Button
                    type="submit"
                    disabled={submitting}
                    className="ornate-button px-6 py-3"
                    data-testid="submit-comment-button"
                  >
                    {submitting ? 'Posting...' : 'Share Experience'}
                  </Button>
                </div>
              </form>
            ) : (
              <div className="card-mystical rounded-xl p-8 text-center mb-12">
                <p className="text-primary/70 mb-4">Sign in to share your experiences</p>
                <Button onClick={() => navigate(authUrlWithNext(location))} className="ornate-button px-6 py-3">
                  Sign In
                </Button>
              </div>
            )}

            <div className="space-y-6">
              {comments.length === 0 ? (
                <p className="text-primary/60 text-center py-8">Be the first to share your experience</p>
              ) : comments.map((comment) => (
                <div key={comment.id} className="card-mystical rounded-xl p-6" data-testid={`comment-${comment.id}`}>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center text-primary font-heading">
                      {comment.user_name.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="font-heading text-primary">{comment.user_name}</span>
                        <span className="text-xs text-primary/50">
                          {new Date(comment.created_at).toLocaleDateString('en-US', {
                            year: 'numeric', month: 'short', day: 'numeric',
                          })}
                        </span>
                      </div>
                      <p className="text-primary/80 leading-relaxed">{comment.content}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Related Articles */}
          {related.length > 0 && (
            <div className="mt-16" data-testid="blog-related">
              <div className="text-xs uppercase tracking-[0.35em] text-primary/60 text-center mb-6 font-heading">
                You might also like to read
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {related.slice(0, 2).map((rp, idx) => (
                  <motion.article
                    key={rp.id}
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: idx * 0.05 }}
                    onClick={() => navigate(`/blog/${rp.slug}`)}
                    className={`card-mystical rounded-2xl overflow-hidden cursor-pointer group ${
                      idx === 1 ? 'hidden md:block' : ''
                    }`}
                    data-testid={`blog-related-${rp.id}`}
                  >
                    <div className="aspect-[16/9] overflow-hidden bg-primary/5">
                      {rp.cover_image_url ? (
                        <img
                          src={rp.cover_image_url}
                          alt=""
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-primary/20 via-primary/10 to-transparent" />
                      )}
                    </div>
                    <div className="p-6 relative">
                      <div className="flex items-center gap-2 text-xs text-primary/70 mb-3 flex-wrap">
                        <span className="px-3 py-1 rounded-full bg-primary/10 text-primary font-medium border border-primary/20">
                          {rp.category}
                        </span>
                        <div className="flex items-center gap-1 ml-auto">
                          <Clock size={14} />
                          <span>{rp.read_time || 1} min read</span>
                        </div>
                      </div>
                      <h2 className="text-xl font-heading font-semibold text-primary line-clamp-2 mb-2">
                        {rp.title}
                      </h2>
                      {rp.subtitle && (
                        <p className="text-sm text-primary/70 line-clamp-2 mb-4">{rp.subtitle}</p>
                      )}
                      <div className="flex items-center gap-2 text-xs text-primary/60">
                        <User size={14} />
                        <span>{rp.author_name}</span>
                      </div>
                    </div>
                  </motion.article>
                ))}
              </div>
            </div>
          )}
        </motion.div>
      </article>

      <WhatsAppCTA className="mt-16 border-t border-primary/20" />
      <InnerCircleBottomBar />
    </div>
  );
};

export default BlogPost;
